import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectLeaseComps } from "@/features/survey/leaseCompsSelectors";
import {
  setCurrentLeaseCompId,
  updateLeaseComp as updateLeaseCompAction,
} from "@/features/survey/leaseCompsSlice";
import {
  leaseCompsApi,
  useUpdateLeaseCompMutation,
} from "@/services/leaseComps";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldButtonSize, EmeraldButtonVariant } from "@emerald/nxcore";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";
import React, { useEffect, useState } from "react";

interface LeaseCompDetailHeaderFormProps {
  leaseComp: LeaseComp;
}

const LeaseCompDetailHeaderForm = ({
  leaseComp,
}: LeaseCompDetailHeaderFormProps) => {
  const dispatch = useAppDispatch();
  const [updateLeaseComp] = useUpdateLeaseCompMutation();
  const [leaseCompName, setLeaseCompName] = useState(leaseComp?.name || "");

  useEffect(() => {
    setLeaseCompName(leaseComp?.name || "");
  }, [leaseComp?.name]);

  // All lease comps in list order (hidden ones excluded from navigation)
  const allLeaseComps = useAppSelector(selectLeaseComps).filter(
    (lc) => !lc.isHidden,
  );
  const currentIndex = allLeaseComps.findIndex(
    (lc) => lc.leaseCompId === leaseComp?.leaseCompId,
  );
  const total = allLeaseComps.length;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < total - 1;

  const navigateTo = (index: number) => {
    const target = allLeaseComps[index];
    if (target) {
      dispatch(setCurrentLeaseCompId({ leaseCompId: target.leaseCompId }));
    }
  };

  const handleBack = () => {
    dispatch(setCurrentLeaseCompId(null));
  };

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setLeaseCompName(event.target.value);
  };

  const handleNameBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = event.target.value.trim();
    if (leaseComp?.leaseCompId && value !== leaseComp?.name) {
      // 1. Update Redux slice so the list item reflects the new name immediately.
      dispatch(
        updateLeaseCompAction({
          leaseCompId: leaseComp.leaseCompId,
          updates: { name: value },
        }),
      );

      // 2. Update RTK Query cache directly so the comparison view reflects the
      //    new name without triggering a /all refetch (which could race with
      //    pending address PATCH requests and overwrite them).
      const patchResult = dispatch(
        leaseCompsApi.util.updateQueryData(
          "getLeaseComps",
          leaseComp.surveyId,
          (draft) => {
            const item = draft.find(
              (lc) => lc.leaseCompId === leaseComp.leaseCompId,
            );
            if (item) {
              item.name = value;
            }
          },
        ),
      );

      // 3. Use RTK Query mutation to trigger survey timestamp update
      updateLeaseComp({
        leaseCompId: leaseComp.leaseCompId,
        updates: { name: value },
        surveyId: leaseComp.surveyId,
      }).catch((_error) => {
        console.error("Failed to update lease comp name");
        // Rollback RTK cache, Redux slice, and local input state on failure.
        patchResult.undo();
        setLeaseCompName(leaseComp?.name || "");
        dispatch(
          updateLeaseCompAction({
            leaseCompId: leaseComp.leaseCompId,
            updates: { name: leaseComp.name },
          }),
        );
      });
    }
  };

  return (
    <div className="LeaseComp-details-header flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-x-1 items-center">
          <EmeraldIconButton
            icon="keyboard_arrow_left"
            onClick={handleBack}
            type="Icon"
          />
          <EmeraldInput
            value={leaseCompName || "Untitled Lease Comp"}
            className="w-64 emerald-standard leaseCompName"
            inputSize="small"
            outlined={false}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
          />
        </div>

        <div className="flex items-center gap-3">
          {/* Lease comp record navigator */}
          {total > 1 && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 bg-white"
              title="Navigate between lease comps"
            >
              <button
                type="button"
                onClick={() => navigateTo(currentIndex - 1)}
                disabled={!hasPrev}
                aria-label="Previous lease comp"
                className="btn-nav-primary group flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary"
              >
                <EmeraldIcon
                  icon="chevron_left"
                  variant="icon"
                  fontsizeInRem={1.1}
                  className="text-primary group-hover:text-white!"
                />
              </button>
              <span className="text-sm text-slate-500 min-w-14 text-center select-none">
                {currentIndex + 1} of {total}
              </span>
              <button
                type="button"
                onClick={() => navigateTo(currentIndex + 1)}
                disabled={!hasNext}
                aria-label="Next lease comp"
                className="btn-nav-primary group flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary"
              >
                <EmeraldIcon
                  icon="chevron_right"
                  variant="icon"
                  fontsizeInRem={1.1}
                  className="text-primary group-hover:text-white!"
                />
              </button>
            </div>
          )}

          <EmeraldButton
            label="See All Lease Comps"
            type={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            nativeProps={{
              title: "See all lease comps",
              type: "button",
              "aria-labelledby": "see-all-lease-comps",
              "aria-label": "See all lease comps",
            }}
            onClick={handleBack}
          />
        </div>
      </div>
      <div className="warning-message text-data bg-info-alert border-info-icon border flex p-2.5 gap-2 text-sm items-center rounded-sm">
        <EmeraldIcon
          icon="info"
          type="filled"
          variant="icon"
          className="text-info-icon"
        />
        <span>
          Edits made to lease comp related fields below will display in this
          survey only. Edits will not update the Site IQ project lease comps or
          Site IQ team data.
        </span>
      </div>
    </div>
  );
};

export default LeaseCompDetailHeaderForm;
